export enum LlmEnum {
  // azure models
  GPT_55 = 'wm-prompt-lib-gpt-5.5',
  GPT_54_PRO = 'wm-prompt-lib-gpt-5.4-pro',
  GPT_54 = 'wm-prompt-lib-gpt-5.4',
  GPT_53_CODEX = 'wm-prompt-lib-gpt-5.3-codex',
  GPT_52 = 'wm-prompt-lib-gpt-5.2',
  GPT_51_CODEX_MAX = 'wm-prompt-lib-gpt-5.1-codex-max',
  GPT_51_CODEX_MINI = 'wm-prompt-lib-gpt-5.1-codex-mini',
  GPT_51 = 'wm-prompt-lib-gpt-5.1',
  GPT_5_PRO = 'wm-prompt-lib-gpt-5-pro',
  GPT_5_MINI = 'wm-prompt-lib-gpt-5-mini',
  GPT_5_NANO = 'wm-prompt-lib-gpt-5-nano',
  CLAUDE_OPUS_47 = 'claude-opus-4-7',
  CLAUDE_OPUS_46 = 'claude-opus-4-6',
  CLAUDE_SONNET_46 = 'claude-sonnet-4-6',
  CLAUDE_OPUS_45 = 'claude-opus-4-5',
  CLAUDE_SONNET_45 = 'claude-sonnet-4-5',
  GPT_41 = 'wm-prompt-lib-gpt-4.1',
  GPT_41_MINI = 'wm-prompt-lib-gpt-4.1-mini',
  GPT_41_NANO = 'wm-prompt-lib-gpt-4.1-nano',

  // keys to connect to non azure models
  OPENAI_RAG = 'openai-assistant',
  GRAPH_RAG = 'graph-rag',
}

export enum ModelProviderEnum {
  OPENAI = 'openAI',
  ANTHROPIC = 'anthropic',
}

export type Llm = {
  deploymentId: string;
  displayName: string;
  contextLimit?: number;
  featureFlag?: string;
  description?: string;
  subDescription?: string;
  imagesAllowed?: boolean;
  reasoning?: number;
  intelligence?: number;
  performance?: number;
  speed?: number;
  hidden?: boolean;
  modelProvider: ModelProviderEnum;
};

export const llms: { [key in LlmEnum]: Llm } = {
  [LlmEnum.GPT_55]: {
    deploymentId: 'wm-prompt-lib-gpt-5.5',
    displayName: 'GPT-5.5',
    contextLimit: 1000000,
    description:
      'Most advanced GPT model for complex reasoning, coding, and professional workflows.',
    subDescription: '(GPT-5.5 1M tokens)',
    imagesAllowed: true,
    reasoning: 5,
    speed: 1,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.GPT_54_PRO]: {
    deploymentId: 'wm-prompt-lib-gpt-5.4-pro',
    displayName: 'GPT-5.4 Pro',
    contextLimit: 1050000,
    description: 'Version of GPT-5.4 that produces smarter and more precise responses.',
    subDescription: '(GPT-5.4 1.05M tokens)',
    imagesAllowed: true,
    reasoning: 5,
    speed: 1,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.GPT_54]: {
    deploymentId: 'wm-prompt-lib-gpt-5.4',
    displayName: 'GPT-5.4',
    contextLimit: 1050000,
    description: 'Best intelligence at scale for agentic, coding, and professional workflows',
    subDescription: '(GPT-5.4 1.05M tokens)',
    imagesAllowed: true,
    reasoning: 5,
    speed: 3,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.GPT_53_CODEX]: {
    deploymentId: 'wm-prompt-lib-gpt-5.3-codex',
    displayName: 'GPT-5.3 Codex',
    contextLimit: 400000,
    description: 'The most capable agentic coding model to date',
    subDescription: '(GPT-5.3 Codex 400k tokens)',
    imagesAllowed: true,
    reasoning: 4,
    speed: 3,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.GPT_52]: {
    deploymentId: 'wm-prompt-lib-gpt-5.2',
    displayName: 'GPT-5.2',
    contextLimit: 400000,
    description: 'Previous frontier model for professional work with configurable reasoning effort',
    subDescription: '(GPT-5.2 400k tokens)',
    imagesAllowed: true,
    reasoning: 4,
    speed: 4,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.GPT_51_CODEX_MAX]: {
    deploymentId: 'wm-prompt-lib-gpt-5.1-codex-max',
    displayName: 'GPT-5.1 Codex Max',
    contextLimit: 400000,
    description: 'A version of GPT-5.1-codex optimized for long running tasks',
    subDescription: '(GPT-5.1 Codex Max 400k tokens)',
    imagesAllowed: true,
    reasoning: 4,
    speed: 4,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.GPT_51_CODEX_MINI]: {
    deploymentId: 'wm-prompt-lib-gpt-5.1-codex-mini',
    displayName: 'GPT-5.1 Codex Mini',
    contextLimit: 400000,
    description: 'Best intelligence at scale for agentic, coding, and professional workflows',
    subDescription: '(GPT-5.1 Codex Mini 400k tokens)',
    imagesAllowed: true,
    reasoning: 4,
    speed: 3,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.GPT_51]: {
    deploymentId: 'wm-prompt-lib-gpt-5.1',
    displayName: 'GPT-5.1',
    contextLimit: 400000,
    description: 'Smaller, more cost-effective, less-capable version of GPT-5.1-Codex',
    subDescription: '(GPT-5.1 400k tokens)',
    imagesAllowed: true,
    reasoning: 4,
    speed: 3,
    modelProvider: ModelProviderEnum.OPENAI,
  },

  [LlmEnum.GPT_5_PRO]: {
    deploymentId: 'wm-prompt-lib-gpt-5-pro',
    displayName: 'GPT-5 Pro',
    contextLimit: 400000,
    featureFlag: 'gpt5_pro',
    description: 'Version of GPT-5 that produces smarter and more precise responses',
    subDescription: '(GPT-5 Pro 400k tokens)',
    imagesAllowed: true,
    reasoning: 4,
    speed: 3,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.GPT_5_MINI]: {
    deploymentId: 'wm-prompt-lib-gpt-5-mini',
    displayName: 'GPT-5 Mini',
    contextLimit: 400000,
    description:
      'Near-frontier intelligence for cost sensitive, low latency, high volume workloads',
    subDescription: '(GPT-5 mini 400k tokens)',
    imagesAllowed: true,
    reasoning: 3,
    speed: 4,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.GPT_5_NANO]: {
    deploymentId: 'wm-prompt-lib-gpt-5-nano',
    displayName: 'GPT-5 Nano',
    contextLimit: 400000,
    description: 'Fastest, most cost-efficient version of GPT-5',
    subDescription: '(GPT-5 nano 400k tokens)',
    imagesAllowed: true,
    reasoning: 2,
    speed: 5,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.CLAUDE_OPUS_47]: {
    deploymentId: 'claude-opus-4-7',
    displayName: 'Opus 4.7',
    contextLimit: 1000000,
    description: 'Flagship Claude model for complex reasoning',
    subDescription: '(Opus 4.7 1m tokens)',
    imagesAllowed: true,
    reasoning: 2,
    speed: 5,
    modelProvider: ModelProviderEnum.ANTHROPIC,
  },
  [LlmEnum.CLAUDE_OPUS_46]: {
    deploymentId: 'claude-opus-4-6',
    displayName: 'Opus 4.6',
    contextLimit: 1000000,
    description: 'Flagship Claude model for complex reasoning',
    subDescription: '(Opus 4.6 1m tokens)',
    imagesAllowed: true,
    reasoning: 2,
    speed: 5,
    modelProvider: ModelProviderEnum.ANTHROPIC,
  },
  [LlmEnum.CLAUDE_SONNET_46]: {
    deploymentId: 'claude-sonnet-4-6',
    displayName: 'Sonnet 4.6',
    contextLimit: 1000000,
    description: 'Balanced Claude model for everyday use',
    subDescription: '(Sonnet 4.6 1m tokens)',
    imagesAllowed: true,
    reasoning: 2,
    speed: 5,
    modelProvider: ModelProviderEnum.ANTHROPIC,
  },
  [LlmEnum.CLAUDE_OPUS_45]: {
    deploymentId: 'claude-opus-4-5',
    displayName: 'Opus 4.5',
    contextLimit: 200000,
    description: 'Claude model for advanced reasoning and complex tasks',
    subDescription: '(Opus 4.5 200k tokens)',
    imagesAllowed: true,
    reasoning: 2,
    speed: 5,
    modelProvider: ModelProviderEnum.ANTHROPIC,
  },
  [LlmEnum.CLAUDE_SONNET_45]: {
    deploymentId: 'claude-sonnet-4-5',
    displayName: 'Sonnet 4.5',
    contextLimit: 1000000,
    description: 'Balanced Claude model for everyday use',
    subDescription: '(Sonnet 4.5 1m tokens)',
    imagesAllowed: true,
    reasoning: 2,
    speed: 5,
    modelProvider: ModelProviderEnum.ANTHROPIC,
  },
  [LlmEnum.GPT_41]: {
    deploymentId: 'wm-prompt-lib-gpt-4.1',
    displayName: 'GPT-4.1',
    contextLimit: 1047576,
    featureFlag: 'gpt4_1',
    description: 'Smartest non-reasoning model',
    subDescription: '(GPT-4.1 1m tokens)',
    imagesAllowed: true,
    intelligence: 4,
    speed: 3,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.GPT_41_MINI]: {
    deploymentId: 'wm-prompt-lib-gpt-4.1-mini',
    displayName: 'GPT-4.1 Mini',
    contextLimit: 1047576,
    featureFlag: 'gpt4_1_mini',
    description: 'Smaller, faster version of GPT-4.1',
    subDescription: '(GPT-4.1 Mini 1m tokens)',
    imagesAllowed: true,
    intelligence: 3,
    speed: 4,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.GPT_41_NANO]: {
    deploymentId: 'wm-prompt-lib-gpt-4.1-nano',
    displayName: 'GPT-4.1 Nano',
    contextLimit: 1047576,
    featureFlag: 'gpt4_1_nano',
    description: 'Fastest, most cost-efficient version of GPT-4.1',
    subDescription: '(GPT-4.1 Nano 1m tokens)',
    imagesAllowed: true,
    intelligence: 2,
    speed: 5,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.OPENAI_RAG]: {
    deploymentId: 'openai-assistant',
    displayName: 'OpenAI Assistant',
    imagesAllowed: false,
    contextLimit: 128000,
    hidden: true,
    modelProvider: ModelProviderEnum.OPENAI,
  },
  [LlmEnum.GRAPH_RAG]: {
    deploymentId: 'graph-rag',
    displayName: 'Graph RAG',
    imagesAllowed: false,
    contextLimit: 128000,
    hidden: true,
    modelProvider: ModelProviderEnum.OPENAI,
  },
};
